import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { CalendarIcon, Globe, Lock, MapPin, Type, Tags } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";
import CountryMultiSelect from "@/components/CountryMultiSelect";
import TripCharacteristicsSelect, { TripCharacteristic } from "@/components/TripCharacteristicsSelect";

interface CreateTripDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTripCreated?: () => void;
}

const CreateTripDialog = ({ open, onOpenChange, onTripCreated }: CreateTripDialogProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [isPublic, setIsPublic] = useState(true);
  const [tripType, setTripType] = useState<"trip" | "vacation">("trip");
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date>();
  const [step, setStep] = useState<1 | 2>(1);
  const [characteristics, setCharacteristics] = useState<TripCharacteristic[]>([]);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!user) {
      toast({
        title: t("createTrip.toastNotLoggedIn"),
        description: t("createTrip.toastNotLoggedInDesc"),
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    const formData = new FormData(e.currentTarget);
    const title = formData.get("title") as string;

    if (selectedCountries.length === 0) {
      toast({
        title: t("createTrip.toastCountryRequired"),
        description: t("createTrip.toastCountryRequiredDesc"),
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    if (tripType === "vacation" && selectedCountries.length > 1) {
      toast({
        title: t("createTrip.toastSingleCountry"),
        description: t("createTrip.toastSingleCountryDesc"),
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    const { data: newTrip, error } = await supabase.from("trips").insert({
      user_id: user.id,
      title,
      countries: selectedCountries,
      start_date: startDate ? format(startDate, "yyyy-MM-dd") : null,
      end_date: null,
      is_public: isPublic,
      trip_type: tripType,
      characteristics: characteristics,
    } as any).select().single();

    setLoading(false);

    if (error || !newTrip) {
      console.error("Error creating trip:", error);
      toast({
        title: t("createTrip.toastError"),
        description: t("createTrip.toastErrorDesc") + " " + error?.message,
        variant: "destructive",
      });
    } else {
      toast({
        title: t("createTrip.toastSuccess"),
        description: t("createTrip.toastSuccessDesc"),
      });
      onOpenChange(false);
      if (onTripCreated) onTripCreated();
      (e.target as HTMLFormElement).reset();
      setSelectedCountries([]);
      setStartDate(undefined);
      setTripType("trip");
      setStep(1);
      setCharacteristics([]);
      
      // Redirect to trip page using slug
      const slug = (newTrip as any).slug || newTrip.id;
      navigate(`/trip/${slug}`);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) setStep(1);
    }}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? t("createTrip.stepTitle") : t("createTrip.stepTitleInfo")}
          </DialogTitle>
        </DialogHeader>

        {step === 1 ? (
          <div className="space-y-4">
            <div className="space-y-3">
              <Label>{t("createTrip.tripTypeQuestion")}</Label>
              <RadioGroup value={tripType} onValueChange={(value) => {
                setTripType(value as "trip" | "vacation");
                if (value === "vacation" && selectedCountries.length > 1) {
                  setSelectedCountries([selectedCountries[0]]);
                }
              }}>
                <div className="flex items-center space-x-2 border rounded-lg p-4 cursor-pointer hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="trip" id="trip" />
                  <Label htmlFor="trip" className="cursor-pointer flex-1">
                    <div className="font-medium text-base">{t("createTrip.tripOption")}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {t("createTrip.tripOptionDesc")}
                    </div>
                  </Label>
                </div>
                <div className="flex items-center space-x-2 border rounded-lg p-4 cursor-pointer hover:bg-accent/50 transition-colors">
                  <RadioGroupItem value="vacation" id="vacation" />
                  <Label htmlFor="vacation" className="cursor-pointer flex-1">
                    <div className="font-medium text-base">{t("createTrip.vacationOption")}</div>
                    <div className="text-sm text-muted-foreground mt-1">
                      {t("createTrip.vacationOptionDesc")}
                    </div>
                  </Label>
                </div>
              </RadioGroup>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="flex-1"
              >
                {t("common.cancel")}
              </Button>
              <Button
                type="button"
                onClick={() => setStep(2)}
                className="flex-1"
              >
                {t("common.next")}
              </Button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Title field - most important, so first */}
            <div className="space-y-2">
              <Label htmlFor="title" className="flex items-center gap-2 text-sm font-medium">
                <Type className="h-4 w-4 text-muted-foreground" />
                {t("createTrip.tripTitle")}
              </Label>
              <Input
                id="title"
                name="title"
                type="text"
                placeholder={t("createTrip.tripTitlePlaceholder")}
                required
                className="h-11"
              />
            </div>

            {/* Country/Destination field */}
            <div className="space-y-2">
              <Label htmlFor="countries" className="flex items-center gap-2 text-sm font-medium">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                {tripType === "vacation" ? t("createTrip.destination") : t("createTrip.countries")}
              </Label>
              <CountryMultiSelect
                value={selectedCountries}
                onChange={setSelectedCountries}
                maxSelections={tripType === "vacation" ? 1 : undefined}
              />
            </div>

            {/* Date field */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <CalendarIcon className="h-4 w-4 text-muted-foreground" />
                {t("createTrip.startDate")}
              </Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-11",
                      !startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, "PPP") : <span>{t("createTrip.selectDate")}</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={setStartDate}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>

            {/* Characteristics field */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2 text-sm font-medium">
                <Tags className="h-4 w-4 text-muted-foreground" />
                {t("tripCharacteristics.label")}
              </Label>
              <TripCharacteristicsSelect
                value={characteristics}
                onChange={setCharacteristics}
              />
            </div>

            {/* Visibility toggle - styled as a card */}
            <div 
              className={cn(
                "flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-colors",
                isPublic ? "bg-accent/30 border-primary/30" : "bg-muted/30"
              )}
              onClick={() => setIsPublic(!isPublic)}
            >
              <div className="flex items-center gap-3">
                {isPublic ? (
                  <Globe className="h-5 w-5 text-primary" />
                ) : (
                  <Lock className="h-5 w-5 text-muted-foreground" />
                )}
                <div>
                  <Label htmlFor="is-public" className="cursor-pointer font-medium">
                    {isPublic ? t("createTrip.public") : t("createTrip.private")}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {isPublic ? t("createTrip.publicDesc") : t("createTrip.privateDesc")}
                  </p>
                </div>
              </div>
              <Switch
                id="is-public"
                checked={isPublic}
                onCheckedChange={setIsPublic}
                onClick={(e) => e.stopPropagation()}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                className="flex-1"
              >
                {t("common.back")}
              </Button>
              <Button type="submit" disabled={loading} className="flex-1">
                {loading ? t("createTrip.creating") : t("createTrip.create")}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateTripDialog;
